import type {
  ApiDashboardElement,
  CoupledDimensionGroupResult,
  DataSourceCandidatesResponse,
  Source,
  WidgetDimensionsResponse,
} from '@highimpact-frontend/api-types';
import { isWidgetDimensionsResponse } from '@highimpact-frontend/api-types';
import {
  dimensionSourceToSource,
  getCoupledDimensionRowLabel,
  getDimensionSourceKey,
} from '@highimpact/source-selector';

import { placeholderSourcesFromElement } from './independentDimensionSelection';
import { getSourceSelectionKey } from './resolveSourcesFromCatalog';
import {
  getCoupledWidgetDimensionConfig,
} from './widgetDimensionSlots';

export type { CoupledWidgetKind } from './widgetDimensionSlots';
export { getCoupledWidgetDimensionConfig };

/**
 * Converts a coupled dimension row into ordered legacy sources for persistence.
 */
export function coupledRowToSources(
  row: CoupledDimensionGroupResult,
  dimensionOrder: readonly string[]
): Source[] {
  return dimensionOrder
    .map((dimensionKey) => row[dimensionKey])
    .filter((source): source is NonNullable<typeof source> => Boolean(source))
    .map((source) => dimensionSourceToSource(source));
}

/**
 * Returns true when a coupled row matches the saved source path order.
 */
export function coupledRowMatchesSources(
  row: CoupledDimensionGroupResult,
  sources: readonly Source[],
  dimensionOrder: readonly string[]
): boolean {
  if (sources.length !== dimensionOrder.length) {
    return false;
  }

  return dimensionOrder.every((dimensionKey, index) => {
    const dimensionSource = row[dimensionKey];
    const savedSource = sources[index];

    return (
      Boolean(dimensionSource) &&
      getDimensionSourceKey(dimensionSource) ===
        getSourceSelectionKey(savedSource)
    );
  });
}

function getCoupledRowKey(row: CoupledDimensionGroupResult): string {
  return Object.values(row)
    .map((source) => getDimensionSourceKey(source))
    .sort()
    .join('|');
}

/**
 * Builds a coupled-group selection map from saved element sources.
 */
export function buildCoupledSelectionFromElement(
  catalog: WidgetDimensionsResponse | null,
  element: ApiDashboardElement,
  groupKey: string,
  dimensionOrder: readonly string[]
): Record<string, CoupledDimensionGroupResult[]> {
  const savedSources = placeholderSourcesFromElement(element);

  if (!catalog || savedSources.length === 0) {
    return { [groupKey]: [] };
  }

  const matchingRows = (catalog.coupled_groups?.[groupKey] ?? []).filter(
    (row) => coupledRowMatchesSources(row, savedSources, dimensionOrder)
  );

  return { [groupKey]: matchingRows };
}

/**
 * Syncs coupled-group selections from a v2 dimensions catalog response.
 */
export function syncCoupledSelectionFromCatalog(
  catalog: DataSourceCandidatesResponse,
  groupKey: string,
  dimensionOrder: readonly string[],
  selection: Readonly<Record<string, readonly CoupledDimensionGroupResult[]>>
): Record<string, CoupledDimensionGroupResult[]> {
  if (!isWidgetDimensionsResponse(catalog)) {
    return {
      [groupKey]: [...(selection[groupKey] ?? [])],
    };
  }

  const catalogRows = catalog.coupled_groups?.[groupKey] ?? [];
  const selectedRows = selection[groupKey] ?? [];

  return {
    [groupKey]: selectedRows.map((selectedRow) => {
      const savedSources = coupledRowToSources(selectedRow, dimensionOrder);

      return (
        catalogRows.find((row) =>
          coupledRowMatchesSources(row, savedSources, dimensionOrder)
        ) ?? selectedRow
      );
    }),
  };
}

/**
 * Returns a stable comparison key for coupled selections.
 */
export function getCoupledSelectionKey(
  selection: Readonly<Record<string, readonly CoupledDimensionGroupResult[]>>
): string {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(selection).map(([groupKey, rows]) => [
        groupKey,
        rows.map((row) => getCoupledRowKey(row)),
      ])
    )
  );
}

/**
 * Returns true when two coupled selections describe the same rows.
 */
export function areCoupledSelectionsEqual(
  left: Readonly<Record<string, readonly CoupledDimensionGroupResult[]>>,
  right: Readonly<Record<string, readonly CoupledDimensionGroupResult[]>>
): boolean {
  return getCoupledSelectionKey(left) === getCoupledSelectionKey(right);
}

export { getCoupledDimensionRowLabel };
